// @flow
// This is an old implementation with Downshift. Consider migrating to a more modern hook solution. See Multiselect for reference.

import * as React from 'react';
import Downshift from 'downshift';
import reactStringReplace from 'react-string-replace';
import { formatQuery } from 'url-lib';
import { t, tp } from 'javascripts/utils/withTranslation';
import FormGroup from '../../../molecules/form-group/react/FormGroup';
import Input from '../../../atoms/input/react/Input';
import Icon from '../../../_particles/icon/react/Icon';

type Props = {
  label: string,
  url: string,
  langId: number,
  pId: number,
  headline: string,
};

type State = {
  value: string,
  highlight: string,
  items: Array<{ text: string, link: string }>,
  initialItems: Array<{ text: string, link: string }>,
  loading: boolean,
  error: boolean,
};

function TopicHeader({ notFound }: { notFound: boolean }): React$Element<any> {
  return (
    <div className="topic-search__header">
      {notFound && (
        <p className="text text--small topic-search__error">
          {t(
            'Es konnten keine Themenseiten mit diesem Suchbegriff gefunden werden.'
          )}
        </p>
      )}
      <p className="headline">{`${
        notFound ? 'Weitere' : ''
      } Top-Themenseiten`}</p>
    </div>
  );
}

function StringHighlighter({
  str,
  substr,
}: {
  str: string,
  substr: string,
}): string {
  if (substr.length < 3) {
    return str;
  }

  return reactStringReplace(str, substr, (match, i) => (
    <em key={i}>{match}</em>
  ));
}

export default class TopicSearch extends React.Component<Props, State> {
  eID: string = 'rsmbwthemenseiten_search';

  queryDefault: { eID: string, [key: string]: any } = {
    eID: this.eID,
    [`${this.eID}[lang]`]: this.props.langId,
    [`${this.eID}[pid]`]: this.props.pId,
  };

  state: State = {
    value: '',
    highlight: '',
    items: [],
    initialItems: [],
    loading: true,
    error: false,
  };

  handleChange: (event: SyntheticInputEvent<HTMLInputElement>) => void = (
    event: SyntheticInputEvent<HTMLInputElement>
  ) => {
    const { target } = event;

    this.setState({
      value: target.value,
      highlight:
        target.value.length >= 3 || target.value.length === 0
          ? target.value
          : this.state.highlight,
    });

    if (target.value.length >= 3) {
      this.fetchTopics(target.value);
    } else if (this.state.items.length === 0) {
      this.fetchTopics();
    } else if (this.state.error || target.value.length === 0) {
      this.setState({
        items: this.state.initialItems,
        error: false,
      });
    }
  };

  fetchTopics: (query?: string) => void = (query?: string) => {
    const searchQuery = {
      ...this.queryDefault,
      [`${this.eID}[query]`]: query,
      q: process.env.NODE_ENV !== 'production' ? query : '',
    };

    const url = `${this.props.url}?${formatQuery(searchQuery)}`;

    this.setState({
      loading: true,
    });

    fetch(url)
      .then((resp) => resp.json())
      .then((data) => {
        this.setState({
          items: data.length > 0 ? data : this.state.initialItems,
          initialItems:
            this.state.initialItems.length === 0
              ? data
              : this.state.initialItems,
          loading: false,
          error: data.length === 0,
        });
      })
      .catch((error) => {
        this.setState({
          loading: false,
          error,
        });
      });
  };

  onSelect: (selection: { link: string, text: string }) => void = (selection: {
    text: string,
    link: string,
  }) => {
    if (selection) {
      this.setState({
        value: selection.text ? selection.text : '',
      });

      if (selection.link) {
        window.location.href = selection.link;
      }
    }
  };

  getA11yStatusMessage({ isOpen, resultCount, previousResultCount }) {
    if (!isOpen) {
      return '';
    }
    if (!resultCount) {
      return t(
        'Es konnten keine Themenseiten mit diesem Suchbegriff gefunden werden.'
      );
    }
    if (resultCount !== previousResultCount) {
      return `${resultCount} ${tp(
        'Eintrag gefunden',
        'Einträge gefunden',
        resultCount
      )} ${t(
        'Navigieren Sie mit den Pfeiltasten. Bestätigen Sie die Auswahl mit der Eingabetaste.'
      )}`;
    }
    return '';
  }

  render(): React$Element<any> {
    return (
      <Downshift
        onChange={this.onSelect}
        itemToString={(item) => (item ? item.text : '')}
        getA11yStatusMessage={this.getA11yStatusMessage.bind(this)}
      >
        {({
          getInputProps,
          getItemProps,
          getLabelProps,
          getMenuProps,
          isOpen,
          getRootProps,
          openMenu,
        }) => (
          <div className="topic-search__inner">
            {this.props.headline && (
              <h2 className="topic-search__headline">{this.props.headline}</h2>
            )}
            <FormGroup label={this.props.label} labelSettings={getLabelProps()}>
              <div
                className="topic-search__search"
                {...getRootProps({}, { suppressRefError: true })}
              >
                <Input
                  {...getInputProps({
                    onChange: this.handleChange,
                    value: this.state.value,
                    onFocus: (event) => {
                      openMenu();
                      this.handleChange(event);
                    },
                    classNames: 'topic-search__input',
                  })}
                />
                {this.state.loading && isOpen ? (
                  <Icon
                    icon="list-loader"
                    className="topic-search__icon list-fetch__loader-icon"
                  />
                ) : (
                  <Icon icon="search" className="topic-search__icon" />
                )}
              </div>
            </FormGroup>
            <div className="topic-search__wrap" {...getMenuProps()}>
              {isOpen ? (
                <div className="topic-search__dropdown">
                  <TopicHeader notFound={this.state.error} />
                  <ul className="topic-search__results">
                    {this.state.items.map((item, index) => (
                      <li
                        className="topic-search__topic"
                        key={item.text}
                        {...getItemProps({
                          index,
                          item,
                        })}
                      >
                        <a
                          className="topic-search__topic-link"
                          href={item.link}
                        >
                          <StringHighlighter
                            str={item.text}
                            substr={this.state.highlight}
                          />
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </Downshift>
    );
  }
}
